import { isAllFacilities, type DirectoryRow } from '@aithora/shared';
import { useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { I, btn, pre, sx, usePage } from './kit';

/**
 * Which building a building section is about (Console V6 README, Facility switcher: "the
 * building sections (On-call, Escalation, Guardian response, Pilot go-live, Schedule) ask
 * 'Pick one building first' when a region is picked"; design geoLayer, settings).
 *
 *   own       a Facility Admin: always their own building (the session's)
 *   picked    Super Admin or Operator with one building picked in the facility switcher:
 *             that building, named to the server by its id
 *   narrowed  a country, region, state or city is picked, not one building: the section
 *             asks to pick one, so a change can never land on the wrong building
 *   none      nothing is picked (every facility): the section shows what is not one
 *             building's (the platform defaults), or asks to pick one when it has none
 *   loading   a place is picked but the building list is not in yet: nothing is shown,
 *             so no section opens on the wrong building or on the defaults first
 */
export type BuildingPick =
  | { mode: 'own'; orgId: string | null; name: string | null }
  | { mode: 'picked'; orgId: string; name: string }
  | { mode: 'narrowed' | 'none' | 'loading'; orgId: null; name: null };

export function useBuilding(): BuildingPick {
  const { session } = useSession();
  const { lens, facilityId } = usePage();
  const { enabled, scope, inView, directory } = useFacilityScope();
  const pick = usePickedIds();
  if (lens.lens !== 'Operator') return { mode: 'own', orgId: facilityId ?? session?.user.organizationId ?? null, name: session?.user.orgName ?? null };
  if (enabled && !isAllFacilities(scope) && !directory.data && !directory.error) return { mode: 'loading', orgId: null, name: null };
  if (!pick) return { mode: isAllFacilities(scope) || !enabled ? 'none' : 'narrowed', orgId: null, name: null };
  if (scope.fac && pick.length === 1) {
    const row = inView.find((f) => f.id === pick[0]);
    return { mode: 'picked', orgId: pick[0], name: row?.name ?? scope.fac };
  }
  return { mode: 'narrowed', orgId: null, name: null };
}

/** At most this many buildings are offered to pick from (the design's list). */
const OFFER = 8;

/**
 * "Pick one building first" (design geoLayer, settings): the buildings in view to pick from,
 * and Show all facilities when a place is picked.
 */
export function PickOneBuilding({ what }: { what: string }) {
  const { scope, inView, setScope } = useFacilityScope();
  const offer: DirectoryRow[] = inView.slice(0, OFFER);
  const choose = (f: DirectoryRow) => setScope({ country: f.country, region: f.region, state: f.state, city: f.city, fac: f.name });
  return (
    <div role="status" data-testid="settings-pick-building" data-screen-label={what} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 2px;max-width:820px;display:flex;flex-direction:column;gap:8px;')}>
      <strong style={sx('font-size:16px;color:#22272E;')}>Pick one building first</strong>
      <span style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;')}>This setting belongs to one building, so a change can never land on the wrong one by accident.</span>
      {offer.length > 0 && (
        <>
          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:4px;')}>Buildings in this view</span>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            {offer.map((f) => (
              <a key={f.id} href="#" onClick={(e) => { pre(e); choose(f); }} style={sx(btn('soft'))}>
                {I(f.name)}
              </a>
            ))}
          </div>
        </>
      )}
      {!isAllFacilities(scope) && (
        <a href="#" onClick={(e) => { pre(e); setScope({}); }} style={sx(btn('ghost') + 'align-self:flex-start;margin-top:4px;')}>Show all facilities</a>
      )}
    </div>
  );
}

/** While the building list loads (mode 'loading'). */
export function BuildingLoading() {
  return <Skeleton rows={4} label="Loading the building" />;
}
